import { z } from "zod";
/** Типы файлов документов */
export const FileTypeEnum = z.enum([
    "work_order",
    "completion_act",
    "rate_book",
    "estimate",
    "unclassified",
]);
/** Статусы обработки файла */
export const FileStatusEnum = z.enum(["uploaded", "recognized", "parsed", "error"]);
/** Файл в ответе API */
export const FileSchema = z.object({
    id: z.number(),
    originalName: z.string(),
    filePath: z.string(),
    fileType: FileTypeEnum,
    objectName: z.string().nullable(),
    periodMonth: z.number().int().min(1).max(12).nullable(),
    periodYear: z.number().int().nullable(),
    headerRow: z.number().int().nullable(),
    headerCol: z.number().int().nullable(),
    fileSize: z.number().int(),
    status: FileStatusEnum,
    errorMessage: z.string().nullable().optional(),
    sheetName: z.string().nullable().optional(),
    createdAt: z.string(),
});
/** Ответ на загрузку пакета файлов */
export const UploadResponseSchema = z.object({
    uploadId: z.string().uuid(),
    files: z.array(z.object({
        id: z.number(),
        originalName: z.string(),
        status: z.string(),
        fileSize: z.number(),
    })),
    totalFiles: z.number(),
});
/** Список файлов */
export const FileListResponseSchema = z.object({
    files: z.array(FileSchema),
    total: z.number(),
});
/** SSE events */
export const SSEProgressEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    filename: z.string(),
    status: z.string(),
    total: z.number(),
    completed: z.number(),
});
export const SSEFileDoneEventSchema = SSEProgressEventSchema.extend({
    fileId: z.number(),
    fileType: FileTypeEnum,
    objectName: z.string().nullable(),
    periodMonth: z.number().nullable(),
    periodYear: z.number().nullable(),
    sheetName: z.string().nullable().optional(),
});
export const SSEFileErrorEventSchema = SSEProgressEventSchema.extend({
    reason: z.string(),
});
export const SSECompleteEventSchema = z.object({
    uploadId: z.string(),
    total: z.number(),
    successful: z.number(),
    failed: z.number(),
    skipped: z.number().optional(),
});
/** SSE событие: результат парсинга книги расценок */
export const SSERateBookParsedEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    filename: z.string(),
    fileId: z.number(),
    totalRows: z.number(),
    parsedCount: z.number(),
    skippedCount: z.number(),
    errors: z.array(z.object({
        row: z.number(),
        reason: z.string(),
    })),
});
/** SSE событие: результат парсинга наряда */
export const SSEWorkOrderParsedEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    filename: z.string(),
    fileId: z.number(),
    totalRows: z.number(),
    parsedCount: z.number(),
    skippedCount: z.number(),
    errors: z.array(z.object({
        row: z.number(),
        reason: z.string(),
    })),
});
/** Ответ на удаление */
export const DeleteResponseSchema = z.object({
    id: z.number(),
    deleted: z.boolean(),
});
/** Ошибки */
export const FileErrorResponseSchema = z.object({
    error: z.string(),
    message: z.string(),
    details: z
        .array(z.object({
        filename: z.string(),
        reason: z.string(),
    }))
        .optional(),
    filename: z.string().optional(),
    maxSizeBytes: z.number().optional(),
});
/** Параметры фильтрации списка файлов */
export const FileListQuerySchema = z.object({
    type: FileTypeEnum.optional(),
    objectName: z.string().optional(),
    periodYear: z.coerce.number().int().optional(),
    periodMonth: z.coerce.number().int().min(1).max(12).optional(),
});
/** SSE событие: сервер запрашивает выбор листа у клиента */
export const SSESheetSelectEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    filename: z.string(),
    sheets: z.array(z.string()),
    alreadyLoaded: z.array(z.string()).optional(),
});
/** SSE событие: доступны листы для выбора при повторной загрузке (некоторые уже загружены) */
export const SSESheetsAvailableEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    filename: z.string(),
    alreadyLoadedSheets: z.array(z.object({
        name: z.string(),
        fileId: z.number(),
        fileType: z.string(),
    })),
    availableSheets: z.array(z.object({ name: z.string() })),
});
/** SSE событие: обработка одного листа успешно завершена */
export const SSESheetDoneEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    sheetIndex: z.number(),
    filename: z.string(),
    sheetName: z.string(),
    fileId: z.number(),
    fileType: z.string(),
    objectName: z.string().nullable(),
    periodMonth: z.number().nullable(),
    periodYear: z.number().nullable(),
});
/** SSE событие: ошибка при обработке одного листа */
export const SSESheetErrorEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    sheetIndex: z.number(),
    filename: z.string(),
    sheetName: z.string(),
    reason: z.string(),
});
/** SSE событие: обновление суммарного прогресса (total/completed по листам) */
export const SSEProgressUpdateEventSchema = z.object({
    uploadId: z.string(),
    total: z.number(),
    completed: z.number(),
});
/** SSE событие: файл пропущен (пользователем или из-за того что все листы уже загружены) */
export const SSEFileSkippedEventSchema = SSEProgressEventSchema.extend({
    reason: z.string(),
});
/** Запрос выбора листа(ов) от клиента */
export const SelectSheetRequestSchema = z
    .object({
    uploadId: z.string(),
    fileIndex: z.number(),
    sheetNames: z.array(z.string()).min(1).optional(),
    sheetName: z.string().optional(),
    cancelled: z.boolean().optional(),
})
    .refine((d) => (d.sheetNames?.length ?? 0) > 0 || d.sheetName || d.cancelled, {
    message: "Either sheetNames, sheetName, or cancelled must be provided",
});
/** Информация о существующем файле при конфликте */
export const ExistingFileInfoSchema = z.object({
    id: z.number(),
    originalName: z.string(),
    fileType: z.string(),
    createdAt: z.string(),
});
/** SSE событие: обнаружен конфликт rate_book (единственный оставшийся тип конфликта) */
export const SSEConflictEventSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    filename: z.string(),
    conflictType: z.enum(["rate-book"]),
    existingFile: ExistingFileInfoSchema,
});
/** Запрос разрешения конфликта от клиента */
export const ResolveConflictRequestSchema = z.object({
    uploadId: z.string(),
    fileIndex: z.number(),
    action: z.enum(["replace", "cancel"]),
});
//# sourceMappingURL=files.js.map